import React, { forwardRef, useState, useEffect } from 'react';
import { Flashcard } from '../data/flashcards';

interface FlashcardCardProps {
  card: Flashcard;
  showAnswer: boolean;
  onCardClick: () => void;
  onSwipe: (direction: 'left' | 'right') => void;
  isAnimating: boolean;
  swipeDirection?: 'left' | 'right' | null;
  showBanner?: boolean;
  bannerDirection?: 'left' | 'right' | null;
  darkMode?: boolean;
  isBookmarked?: boolean;
  onToggleBookmark?: () => void;
}

const FlashcardCard = forwardRef<HTMLDivElement, FlashcardCardProps>(
  ({ card, showAnswer, onCardClick, onSwipe, isAnimating, swipeDirection, showBanner, bannerDirection, darkMode, isBookmarked, onToggleBookmark }, ref) => {
    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [hasSwiped, setHasSwiped] = useState(false);
    const [swipeProcessed, setSwipeProcessed] = useState(false);

    const handleTouchStart = (e: React.TouchEvent) => {
      const touch = e.touches[0];
      setStartPos({ x: touch.clientX, y: touch.clientY });
      setIsDragging(true);
      
      // Prevent page scrolling during touch
      document.body.style.overflow = 'hidden';
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      if (!isDragging) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - startPos.x);
      const deltaY = Math.abs(touch.clientY - startPos.y);
      
      // Only prevent default if it's clearly a horizontal swipe
      if (deltaX > 20 && deltaX > deltaY) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (!isDragging || swipeProcessed) return;
      setIsDragging(false);
      
      // Restore page scrolling
      document.body.style.overflow = '';
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startPos.x;
      const deltaY = Math.abs(touch.clientY - startPos.y);
      const swipeThreshold = 100;
      
      // Only process as swipe if it's clearly horizontal and long enough
      if (Math.abs(deltaX) > swipeThreshold && Math.abs(deltaX) > deltaY) {
        console.log('Touch swipe detected:', deltaX > 0 ? 'right' : 'left');
        setHasSwiped(true);
        setSwipeProcessed(true);
        onSwipe(deltaX > 0 ? 'right' : 'left');
      }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
      setStartPos({ x: e.clientX, y: e.clientY });
      setIsDragging(true);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
      if (!isDragging) return;
      // Don't update drag offset for visual feedback - just track for swipe detection
    };



    useEffect(() => {
      const handleGlobalMouseUp = (e: MouseEvent) => {
        if (isDragging && !swipeProcessed) {
          setIsDragging(false);
          
          const deltaX = e.clientX - startPos.x;
          const swipeThreshold = 100;
          
          if (Math.abs(deltaX) > swipeThreshold) {
            console.log('Mouse swipe detected:', deltaX > 0 ? 'right' : 'left');
            setHasSwiped(true);
            setSwipeProcessed(true);
            onSwipe(deltaX > 0 ? 'right' : 'left');
          }
        }
      };

      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
    }, [isDragging, startPos, onSwipe, swipeProcessed]);

    // Reset hasSwiped when card changes
    useEffect(() => {
      setHasSwiped(false);
      setSwipeProcessed(false);
    }, [card.id]);

    // Cleanup: restore scrolling when component unmounts
    useEffect(() => {
      return () => {
        document.body.style.overflow = '';
      };
    }, []);

    const getDifficultyColor = (difficulty: string) => {
      switch (difficulty) {
        case 'easy': return 'bg-green-500';
        case 'medium': return 'bg-yellow-500';
        case 'hard': return 'bg-red-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div
        ref={ref}
        className={`relative w-full max-w-[28rem] mx-auto rounded-2xl shadow-none sm:shadow-2xl cursor-pointer flex flex-col select-none ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        } ${isAnimating ? 'animate-card-float' : ''
        } ${swipeDirection === 'left' ? 'animate-swipe-left' : ''
        } ${swipeDirection === 'right' ? 'animate-swipe-right' : ''
        } ${isDragging ? 'z-50' : ''}`}
        style={{ 
          height: 'min(40rem, 85vh)',
          minHeight: '24rem'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={() => {
          setIsDragging(false);
          document.body.style.overflow = '';
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onClick={(e) => {
          if (hasSwiped) {
            e.preventDefault();
            setHasSwiped(false);
            return;
          }
          // Small delay to ensure swipe detection completes first
          setTimeout(() => {
            if (!isDragging && !swipeProcessed) {
              onCardClick();
            }
          }, 50);
        }}
      >
        {/* Header with gradient background - Only show when question is displayed */}
        {!showAnswer && (
          <div className={`h-32 sm:h-40 lg:h-48 rounded-t-2xl relative overflow-hidden ${
            darkMode 
              ? 'bg-gradient-to-br from-gray-700 to-gray-600' 
              : 'bg-gradient-to-br from-tinder-pink to-tinder-orange'
          }`}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="text-4xl mb-2">🏠</div>
                <div className="text-sm font-semibold">RealEstart</div>
              </div>
            </div>
          </div>
        )}

                 {/* Content area */}
         <div className="flex flex-col h-full">
           {/* Title and category - Fixed at top */}
           <div className="p-4 sm:p-6 lg:p-8 pb-2 sm:pb-4 flex-shrink-0">
            <div className="flex justify-between items-start">
              <div>
                <h2 className={`text-lg sm:text-xl font-bold mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {card.category}
                </h2>
                <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Question #{card.id}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs text-white font-semibold ${getDifficultyColor(card.difficulty)}`}>
                  {card.difficulty}
                </span>
                {onToggleBookmark && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleBookmark();
                    }}
                    className={`group relative p-2 rounded-full transition-all duration-200 hover:scale-110 ${
                      isBookmarked 
                        ? 'bg-red-500 text-white shadow-lg hover:bg-red-600' 
                        : darkMode 
                          ? 'bg-gray-600 bg-opacity-50 text-gray-300 hover:bg-red-900 hover:text-red-300 border border-gray-500 border-opacity-50 hover:border-red-400'
                          : 'bg-gray-200 bg-opacity-50 text-gray-400 hover:bg-red-50 hover:text-red-500 border border-gray-200 border-opacity-50 hover:border-red-200'
                    }`}
                  >
                    <svg 
                      className={`w-5 h-5 transition-all duration-200 ${
                        isBookmarked ? 'fill-current' : 'fill-gray-500 group-hover:fill-red-500'
                      }`} 
                      viewBox="0 0 24 24" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

                     {/* Question/Answer - Expandable area */}
           <div className="px-4 sm:px-6 lg:px-8 flex-1 overflow-hidden flex flex-col">
            {!showAnswer ? (
              <div className="flex items-center justify-center h-full">
                <p className={`leading-relaxed text-base sm:text-lg lg:text-xl text-center px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {card.question}
                </p>
              </div>
            ) : (
              <div className="flex flex-col h-full w-full min-h-0">
                {card.explanation ? (
                  <>
                    {/* Answer - Centered in upper portion when explanation exists */}
                    <div className="flex-shrink-0 flex items-center justify-center text-center w-full px-2 py-4 sm:py-6">
                      <div>
                        <p className={`leading-relaxed text-base sm:text-lg lg:text-xl font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          <strong className={`block mb-2 text-sm sm:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Answer:</strong>
                          <span className="block">{card.answer}</span>
                        </p>
                      </div>
                    </div>
                    {/* Explanation - Scrollable below */}
                    <div className="flex-1 overflow-y-auto min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                      <div className={`p-3 sm:p-4 rounded-lg mx-2 mb-2 ${darkMode ? 'bg-blue-900 bg-opacity-50' : 'bg-blue-50'}`}>
                        <p className={`text-xs sm:text-sm leading-relaxed ${darkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                          <strong>Explanation:</strong> {card.explanation}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Answer - Fully centered when no explanation */
                  <div className="flex-1 flex items-center justify-center text-center w-full px-2">
                    <div>
                      <p className={`leading-relaxed text-base sm:text-lg lg:text-xl font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        <strong className={`block mb-2 text-sm sm:text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Answer:</strong>
                        <span className="block">{card.answer}</span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

                     {/* Bottom section - Fixed at bottom */}
           <div className="p-4 sm:p-6 lg:p-8 pt-2 sm:pt-4 pb-2 sm:pb-4 flex-shrink-0">
            <div className={`pt-2 sm:pt-4 ${darkMode ? '' : ''}`}>
              {/* My Study Note section - temporarily hidden
              <div className="flex justify-between items-center">
                <div>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>My Study Note</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Click to {showAnswer ? 'hide' : 'reveal'} answer</p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className={`rounded-lg p-2 ${darkMode ? 'bg-pink-900 bg-opacity-50' : 'bg-pink-100'}`}>
                    <div className={`text-xs font-semibold ${darkMode ? 'text-pink-200' : 'text-pink-800'}`}>STUDY</div>
                    <div className={`text-xs ${darkMode ? 'text-pink-300' : 'text-pink-600'}`}>MODE</div>
                  </div>
                </div>
              </div>
              */}
              
              {/* Keyboard shortcuts hint - Hide on mobile */}
              <div className={`hidden sm:block mt-4 pt-4`}>
                {/* Keyboard Controls Label */}
                <div className="text-center mb-3">
                  <span className={`text-xs font-medium tracking-wide uppercase ${
                    darkMode 
                      ? 'text-gray-500/40' 
                      : 'text-gray-400/50'
                  }`}>
                    Keyboard Controls
                  </span>
                </div>
                
                <div className={`flex justify-center space-x-6 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <div className="flex items-center space-x-1">
                    <kbd className={`px-2 py-1 rounded font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>←</kbd>
                    <span>Don't Know</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <kbd className={`px-2 py-1 rounded font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>Space</kbd>
                    <span>Reveal</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <kbd className={`px-2 py-1 rounded font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>→</kbd>
                    <span>Know</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Swipe Indicators - Only visible on mobile */}
        <div className="sm:hidden absolute bottom-4 left-0 right-0 pointer-events-none">
          {/* Swipe Controls Label */}
          <div className="text-center mb-3">
            <span className={`text-xs font-medium tracking-wide uppercase ${
              darkMode 
                ? 'text-gray-500/40' 
                : 'text-gray-400/50'
            }`}>
              Swipe Controls
            </span>
          </div>
          
          <div className={`flex justify-center space-x-6 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <div className="flex items-center space-x-1">
              <kbd className={`px-2 py-1 rounded font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>←</kbd>
              <span>Don't Know</span>
            </div>
            <div className="flex items-center space-x-1">
              <kbd className={`px-2 py-1 rounded font-mono ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>→</kbd>
              <span>Know</span>
            </div>
          </div>
        </div>

        {/* Status Banners */}
        {showBanner && bannerDirection && (
          <div className="absolute inset-0 pointer-events-none">
            {bannerDirection === 'right' && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full font-bold shadow-lg transform transition-all duration-300 animate-pulse">
                ✓ KNOWN
              </div>
            )}
            {bannerDirection === 'left' && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold shadow-lg transform transition-all duration-300 animate-pulse">
                ✕ REVIEW
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

FlashcardCard.displayName = 'FlashcardCard';

export default FlashcardCard; 